import { useEffect, useState } from 'react';

import Tippy from '@tippyjs/react';
import cs from 'classnames';
import { useRecoilValue } from 'recoil';

import { useChatContextApi } from 'lib/context/MyScribeAI/MyScribeTemplateContext/ChatContext/ChatContext';
import { iaRa } from 'lib/helpers/utility';

import Avatar from 'components/shared/Avatar/Avatar';
import useBreadcrumb from 'components/shared/Breadcrumb/useBreadcrumb';
import Icon from 'components/shared/Icon/Icon';
import Modal from 'components/shared/Modal/Modal';
import { userState } from 'components/state';

import { componentButtons } from '../../constants';
import useComponentAdd from '../../hooks/useComponentAdd';

import ComponentSelectorModal from './ComponentSelectorModal';

const getFinalTags = (tagIds) => {
  const finalTags = new Map();

  for (const item of tagIds) {
    const parts = item.split(':');
    const category = parts[0];
    const id =
      category === 'patient_history' ? `${parts[1]}${parts[2] ? `:${parts[2]}` : ''}` : parts[1]; // Format for patient_history

    const tag = componentButtons.find((componentButton) => componentButton?.id === category);

    if (tag) {
      if (finalTags.has(category)) {
        finalTags.get(category).selectedIds.push(id);
      } else {
        finalTags.set(category, { ...tag, selectedIds: [id] });
      }
    }
  }

  return Array.from(finalTags.values());
};

const UserPrompt = ({ message, tags, attachments = [] }) => {
  const breadcrumb = useBreadcrumb() || {};
  const [finalMessage, setFinalMessage] = useState(message);
  const patient = useRecoilValue(userState);
  const { setUserPrompt } = useChatContextApi() || {};
  const { inputTags, selectedTableRows, setInputTags, setSelectedTableRows } =
    useComponentAdd() || {};
  const [promptTags, setPromptTags] = useState(inputTags || []);
  const [componentToAdd, setComponentToAdd] = useState(null);
  const [selectedImage, setSelectedImage] = useState();

  useEffect(() => {
    if (!tags?.length) {
      setPromptTags(inputTags);
      setInputTags([]);
    }
  }, []);

  useEffect(() => {
    if (!tags?.length) return;
    const finalTags = getFinalTags(tags);

    setSelectedTableRows(() => {
      const obj = {};
      finalTags.map((tag) => {
        obj[tag?.id] = {
          selectedTags: tag?.selectedIds
        };
      });
      return obj;
    });
    setPromptTags(finalTags);
  }, [tags]);

  useEffect(() => {
    setFinalMessage(message);
    setUserPrompt(message);
  }, [message]);

  const onTagPreview = () => {
    // const clickedComponent = promptTags.find((tag) => tag.id === id);
    // setComponentToAdd(clickedComponent);
  };

  const getTagClassName = (id) => {
    return cs(
      'text-primary-700 !bg-primary-50 hover:!bg-primary-100',
      selectedTableRows?.[id]?.status === 'partial' && !tags?.length
        ? '!bg-warning-100 hover:!bg-warning-200 !text-warning-500'
        : ''
    );
  };

  const closeModal = () => setSelectedImage(null);

  return (
    <div className="flex flex-col items-end gap-3">
      <div className="flex items-center gap-2">
        <div className="text-base font-600 text-neutral-800">
          {patient?.f_name} {patient?.l_name}
        </div>
        <Avatar
          firstName={patient?.f_name}
          lastName={patient?.l_name}
          id={patient?.id}
          image={patient?.header_photo}
          size="large"
          className="rounded-full"
          background="linear-gradient(98deg, rgba(19, 184, 255, 1) 0%, rgba(0, 103, 140, 1) 100%)"
          autoBackground={false}
        />
      </div>

      <div className="mx-[40px] flex flex-grow flex-col">
        <div className="w-full rounded-lg bg-primary-50">
          <div className="mx-1 mt-1 flex items-center justify-end gap-1">
            {iaRa(attachments)?.map((attachment) => (
              <Tippy
                key={attachment?.url}
                content={attachment?.name}
                placement="top"
                className="tippy-dark"
                arrow={false}>
                <div>
                  {attachment?.type?.includes('image') ? (
                    <img
                      src={attachment.url}
                      width={75}
                      height={75}
                      className="cursor-pointer rounded"
                      onClick={() => setSelectedImage(attachment.url)}
                    />
                  ) : (
                    <div className="inline-block rounded bg-neutral-100 p-1 text-black">
                      <a
                        href={attachment.url}
                        download={attachment.name}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-2 text-blue-800 underline">
                        {attachment?.name}
                      </a>
                    </div>
                  )}
                </div>
              </Tippy>
            ))}
          </div>
          <div className="px-4 py-2">{finalMessage}</div>
          <div className="flex flex-wrap items-center">
            {promptTags.map((tag) => (
              <span
                onClick={() => onTagPreview(tag.id)}
                key={tag.id}
                className={cs(
                  'relative my-1 ml-1 cursor-pointer rounded bg-primary-50 p-1 px-2 text-sm text-primary-700',
                  getTagClassName(tag.id)
                )}>
                #{tag?.text}
              </span>
            ))}
          </div>
        </div>
      </div>
      {componentToAdd && (
        <ComponentSelectorModal
          isView
          componentToAdd={componentToAdd}
          onClose={() => setComponentToAdd(null)}
          breadcrumb={breadcrumb}
        />
      )}

      {selectedImage && (
        <Modal
          isOpen={!!selectedImage}
          handleClose={closeModal}
          className="flex h-full w-full items-center justify-center bg-transparent"
          overlayClassName="bg-black !bg-opacity-70 backdrop-blur-sm"
          isFooter={false}
          disableHeader>
          <div className="relative h-full w-full" onClick={(e) => e.stopPropagation()}>
            <Icon
              color="white"
              size={20}
              icon="close-thick-linear"
              onClick={closeModal}
              className="fixed right-5 top-5"
            />
            <img src={selectedImage} alt="image" className="z-50 max-h-[90vh] max-w-full rounded" />
          </div>
        </Modal>
      )}
    </div>
  );
};

export default UserPrompt;
